/**
 * CRM Object Slideout - Displays CRM records in a resizable slideout panel.
 *
 * Mounted by `SlideoutRenderer` only when the top entry of the slideout
 * stack is an `entity` — exactly one slideout component exists in the DOM
 * at any moment. Closing this slideout pops the top entry off the stack;
 * if a previous entry remains, the renderer mounts that next.
 *
 * The slideout stack still tracks the full history (URL-synced) so back-
 * navigation works across kinds (entity → proposal → entity), but the
 * underlying state of covered entries does NOT survive a layer being
 * pushed on top — the previous slideout unmounts and re-mounts when
 * revealed. React Query data cached during the first mount means re-
 * mounting paints content immediately without a fresh network round-trip.
 */

import { type ReactElement, useCallback, useEffect, useRef } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { useOverlayOrder } from '@/contexts/OverlayOrderContext';
import { StackingLevelProvider } from '@/contexts/StackingContext';

import { SlideoutOriginProvider } from '../../contexts/SlideoutOriginContext';
import {
  type SlideoutEntry,
  useSlideoutStackContext,
} from '../../contexts/SlideoutStackContext';
import { useSlideoutStack } from '../../hooks/useSlideoutStack';
import { SingleSlideout } from '../single-slideout';

// Re-export for backward compatibility — canonical source is SlideoutStackContext
// eslint-disable-next-line react-refresh/only-export-components -- re-export for backward compatibility
export { SLIDEOUT_TIMELINE_ID_PARAM } from '../../contexts/SlideoutStackContext';

export interface CrmObjectSlideoutProps {
  /** Stack entry this slideout renders. Must be `kind: 'entity'`. */
  entry: SlideoutEntry;
  /** Minimum width in pixels (default: 400) */
  minWidth?: number;
  /** Maximum width in pixels (default: 900) */
  maxWidth?: number;
}

/**
 * CRM Object Slideout component.
 *
 * Renders a single CRM record slideout for the entry passed in. The
 * `SlideoutRenderer` dispatcher only mounts this when the top of the
 * slideout stack is an `entity` entry, so this component always
 * represents the visible top.
 */
export function CrmObjectSlideout({
  entry,
  minWidth = 400,
  maxWidth = 900,
}: CrmObjectSlideoutProps): ReactElement | null {
  const { currentOrg } = useOrg();
  const { stack, popSlideout, dismissToProposalBase } = useSlideoutStack();
  const { registerPushGuard, unregisterPushGuard } = useSlideoutStackContext();

  // Register with the overlay-order system so this slideout layers above
  // any modals/overlays opened earlier. Passing `entry.id` re-registers
  // when the dispatcher swaps this component to a new entity (entity A →
  // entity B keeps the same instance mounted), so the new top entity
  // bumps above anything opened in between.
  const overlayOrder = useOverlayOrder(entry.id);

  // Track the current slideout's unsaved-changes guard via a ref. The
  // SingleSlideout's guard propagates up here so we can register it with
  // the stack context for `pushSlideout` interception.
  const guardRef = useRef<((action: () => void) => boolean) | null>(null);

  const handleGuardChange = useCallback(
    (guard: ((action: () => void) => boolean) | null) => {
      guardRef.current = guard;
      if (guard) {
        registerPushGuard(guard);
      } else {
        unregisterPushGuard();
      }
    },
    [registerPushGuard, unregisterPushGuard]
  );

  // Clean up the push guard when this slideout unmounts (e.g. user popped
  // it, or another kind was pushed on top and the dispatcher tore us down).
  useEffect(() => {
    return () => {
      unregisterPushGuard();
    };
  }, [unregisterPushGuard]);

  if (!currentOrg || entry.kind !== 'entity') {
    return null;
  }

  return (
    <StackingLevelProvider level={overlayOrder}>
      {/* Clicks inside this slideout's content are "from within the stack" —
          useOpenSlideout resolves them to pushSlideout (drill-down layers). */}
      <SlideoutOriginProvider entry={entry}>
        <SingleSlideout
          key={entry.id}
          objectId={entry.id}
          minWidth={minWidth}
          maxWidth={maxWidth}
          onClose={dismissToProposalBase}
          onBack={popSlideout}
          showBackButton={stack.length > 1}
          onGuardChange={handleGuardChange}
        />
      </SlideoutOriginProvider>
    </StackingLevelProvider>
  );
}
